import { uniq } from 'lodash';
import { FC, useState } from 'react';
import { Avatar, Menu, MenuItem, styled } from '@mui/material';
import { AdminUserRealm } from 'further-types/admin-user';
import useSwitchOrganisation from 'hooks/auth/useSwitchOrganisation';
import { superOrgLogo } from 'adminConstants/files';
import Chip from 'components/Chip';

const AvatarWrapper = styled(Avatar)({
  width: '50px',
  height: '50px',
  borderStyle: 'solid',
  borderWidth: 4,
  borderColor: 'grey.200',
  cursor: 'pointer',
});

const RealmPill = styled(Chip)({
  marginLeft: '1rem',
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(theme.spacingSizes.sm),
}));

type Props = {
  otherOrgs?: Array<{
    auth0OrgId?: string;
    firmName?: string;
    firmLogo?: string;
    realm?: AdminUserRealm;
  }>;
  firm?: {
    investmentFirmLogo?: string;
    firmName?: string;
  };
  realm?: AdminUserRealm;
};

const OrganisationSwitcher: FC<Props> = ({ otherOrgs, firm, realm }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const onSwitchOrganisation = useSwitchOrganisation();
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getFallbackAvatarText = (firmName?: string) => {
    return firmName
      ? firmName
          .split(' ')
          .map((word) => word[0])
          .slice(0, 2)
          .join('')
      : '';
  };

  if (!otherOrgs?.length) return null;

  const uniqRealms = uniq([...otherOrgs.map((org) => org.realm), realm]);
  const showRealmPill = uniqRealms.length > 1;

  return (
    <>
      <AvatarWrapper
        src={
          realm === AdminUserRealm.Super
            ? superOrgLogo
            : firm?.investmentFirmLogo
        }
        onClick={handleClick}
      >
        {realm == AdminUserRealm.Super
          ? 'Fu'
          : getFallbackAvatarText(firm?.firmName)}
      </AvatarWrapper>
      <Menu open={open} onClose={handleClose} anchorEl={anchorEl}>
        {otherOrgs
          ?.slice()
          .sort((a, b) => (a.firmName ?? '').localeCompare(b.firmName ?? ''))
          .map((org) => (
            <StyledMenuItem
              key={org.auth0OrgId}
              onClick={() => onSwitchOrganisation(org)}
            >
              <Avatar
                sx={{ width: 32, height: 32 }}
                src={
                  org.realm === AdminUserRealm.Super
                    ? superOrgLogo
                    : org.firmLogo
                }
              >
                {getFallbackAvatarText(org.firmName)}
              </Avatar>
              <span>
                Switch to{' '}
                {org.realm === AdminUserRealm.Super ? 'Further' : org.firmName}
              </span>
              {showRealmPill ? (
                <RealmPill variant="success" small>
                  {org.realm === AdminUserRealm.Eis ? 'S/EIS' : org.realm}
                </RealmPill>
              ) : null}
            </StyledMenuItem>
          ))}
      </Menu>
    </>
  );
};

export default OrganisationSwitcher;
